import { useState, type ComponentType, type ReactNode } from "react"
import { Link } from "wouter"
import Brandmark from "../assets/brandmark.svg?react"
import Wordmark from "../assets/logo.svg?react"
import CloseIcon from "../assets/icon-close.svg?react"
import MenuIcon from "../assets/icon-menu.svg?react"
import styles from "./PortalLayout.module.css"

export type PortalNavItem = {
  href: string
  label: string
  icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>
  current?: boolean
}

/**
 * The signed-in portal chrome — a navy rail and header framing an off-white
 * content panel, which the rounded corners let the navy show through.
 *
 * The rail keeps its labels in `aria-label` rather than only in the text it
 * reveals when expanded, so collapsed icons still announce themselves.
 *
 * `highlightCurrent` is opt-in because the design system gives the rail item
 * Enable, Hover and Active with no separate selected variant, and its own
 * frames leave the current page in Enable. A rail of one item follows that; a
 * rail of many needs to say which section you are in, and borrows Active to do
 * it.
 */
export function PortalLayout({
  navLabel,
  items,
  header,
  highlightCurrent = false,
  children,
}: {
  navLabel: string
  items: PortalNavItem[]
  header?: ReactNode
  highlightCurrent?: boolean
  children: ReactNode
}) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className={styles.shell}>
      <nav
        className={`${styles.rail} ${expanded ? styles.railExpanded : ""}`}
        aria-label={navLabel}
      >
        <div className={styles.brand}>
          {expanded ? (
            <Wordmark
              className={styles.wordmark}
              role="img"
              aria-label="RAWA"
            />
          ) : (
            <Brandmark
              className={styles.brandmark}
              role="img"
              aria-label="RAWA"
            />
          )}
        </div>

        <button
          type="button"
          className={styles.railItem}
          aria-expanded={expanded}
          aria-label={expanded ? "Close menu" : "Open menu"}
          onClick={() => setExpanded(!expanded)}
        >
          <span className={styles.iconSlot}>
            {expanded ? (
              <CloseIcon className={styles.closeGlyph} aria-hidden="true" />
            ) : (
              <MenuIcon className={styles.menuGlyph} aria-hidden="true" />
            )}
          </span>
          {expanded && <span className={styles.railLabel}>Close menu</span>}
        </button>

        {items.map(({ href, label, icon: Icon, current }) => (
          <Link
            key={href}
            href={href}
            className={`${styles.railItem} ${
              highlightCurrent && current ? styles.railItemCurrent : ""
            }`}
            aria-label={label}
            aria-current={current ? "page" : undefined}
          >
            <span className={styles.iconSlot}>
              <Icon className={styles.navGlyph} aria-hidden />
            </span>
            {expanded && <span className={styles.railLabel}>{label}</span>}
          </Link>
        ))}
      </nav>

      <div className={styles.column}>
        <header className={styles.header}>{header}</header>
        <main className={styles.panel}>{children}</main>
      </div>
    </div>
  )
}
